import React, { useMemo, useState } from 'react';
import TableSearchBar from '../ui/TableSearchBar';
import { useMedia } from '../../hooks/useMedia';
import {
  filterCountryTree,
  flattenCountryTreeForExport,
  formatRoiMoney,
  formatRoiNum,
  formatRoiPct,
  formatRoiEcpm,
  roiToneClass,
} from '../../utils/report/roiView';
import { showToast } from '../../hooks/useToast';
import { downloadCsv, downloadExcel } from '../../utils/tableExport';
import RoiTreeEntityIcon from './RoiTreeEntityIcon';
import { EmptyIcon } from '../ui/Icon';

const BASE_COLS = [
  { id: 'label', label: 'Country / Account / Package / Site', type: 'dimension' },
  { id: 'date', label: 'Date', type: 'dimension' },
  { id: 'adsSpend', label: 'Ads spend', type: 'metric' },
  { id: 'impressions', label: 'Impressions', type: 'metric' },
  { id: 'clicks', label: 'Clicks', type: 'metric' },
  { id: 'ctr', label: 'CTR', type: 'metric' },
  { id: 'ecpm', label: 'Ads eCPM', type: 'metric' },
  { id: 'earn', label: 'Earn', type: 'metric' },
  { id: 'otherExpenses', label: 'Expenses', type: 'metric' },
  { id: 'profitSpend', label: 'Profit (spend)', type: 'metric' },
  { id: 'profitExpense', label: 'Profit (expenses)', type: 'metric' },
  { id: 'roiSpendPercent', label: 'ROI spend %', type: 'metric' },
  { id: 'roiExpensePercent', label: 'ROI expenses %', type: 'metric' },
];

function money(n, currency = 'USD') {
  const formatted = formatRoiMoney(n, currency);
  if (currency === 'USD' || !currency) return formatted.replace(/^US\$/, '$');
  return formatted;
}

function pct(n) {
  return formatRoiPct(n);
}

function SortHeader({ label, active, dir, onClick }) {
  return (
    <button
      type="button"
      className={`th-sort-btn${active ? ` is-sorted-${dir}` : ''}`}
      onClick={onClick}
      title={`Sort by ${label}`}
    >
      <span>{label}</span>
      <span className="th-sort-icon" aria-hidden>{active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </button>
  );
}

function sortValueFor(row, colId) {
  if (colId === 'label') return row.label ?? '';
  if (colId === 'date') return row.date || row.dateLabel || '';
  const n = Number(row[colId]);
  return Number.isFinite(n) ? n : null;
}

function compareRows(a, b, colId, dir) {
  const av = sortValueFor(a, colId);
  const bv = sortValueFor(b, colId);
  const emptyA = av == null || av === '';
  const emptyB = bv == null || bv === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  let diff;
  if (typeof av === 'number' && typeof bv === 'number') {
    diff = av - bv;
  } else {
    diff = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  }
  if (diff === 0 && colId !== 'label') {
    diff = String(a.label || '').localeCompare(String(b.label || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  return dir === 'asc' ? diff : -diff;
}

/** Sort country → account → package → date by the active column (dashboard-style). */
function sortCountryTree(tree, colId, dir) {
  return [...tree]
    .map((country) => {
      if (country.flatMode) {
        const packages = (country.packages || [])
          .map((pkg) => {
            const days = [...(pkg.days || [])].sort((a, b) => compareRows(a, b, colId, dir));
            return { ...pkg, days };
          })
          .sort((a, b) => compareRows(a, b, colId, dir));
        return { ...country, packages, accounts: [] };
      }
      const accounts = (country.accounts || [])
        .map((account) => {
          const packages = (account.packages || [])
            .map((pkg) => {
              const days = [...(pkg.days || [])].sort((a, b) => compareRows(a, b, colId, dir));
              return { ...pkg, days };
            })
            .sort((a, b) => compareRows(a, b, colId, dir));
          return { ...account, packages };
        })
        .sort((a, b) => compareRows(a, b, colId, dir));
      return { ...country, accounts };
    })
    .sort((a, b) => compareRows(a, b, colId, dir));
}

function TreeChevron({ open, hasChildren }) {
  if (!hasChildren) {
    return <span className="roi-tree-chevron roi-tree-chevron--spacer" aria-hidden />;
  }
  return (
    <span className={`roi-tree-chevron${open ? ' is-open' : ''}`} aria-hidden>
      ▶
    </span>
  );
}

function renderMetric(row, colId, currency = 'USD') {
  const earnOnly = row.targetType === 'site' || row.earnOnly || row.level === 'site';
  // Sites are earn-first: blank Ads spend / ROI spend %, but show engagement + eCPM.
  if (earnOnly && ['adsSpend', 'roiSpendPercent'].includes(colId)) {
    return '—';
  }
  if (colId === 'adsSpend') return money(row.adsSpend, currency);
  if (colId === 'impressions') return formatRoiNum(row.impressions);
  if (colId === 'clicks') return formatRoiNum(row.clicks);
  if (colId === 'ctr') return pct(row.ctr);
  if (colId === 'ecpm') {
    const v = formatRoiEcpm(row.ecpm, currency);
    return typeof v === 'string' && currency === 'USD' ? v.replace(/^US\$/, '$') : v;
  }
  if (colId === 'earn') return money(row.earn, currency);
  if (colId === 'otherExpenses') return money(row.otherExpenses, currency);
  if (colId === 'profitSpend') return money(row.profitSpend, currency);
  if (colId === 'profitExpense') return money(row.profitExpense, currency);
  if (colId === 'roiSpendPercent') return pct(row.roiSpendPercent);
  if (colId === 'roiExpensePercent') return pct(row.roiExpensePercent);
  return '—';
}

function metricCellClass(row, colId) {
  if (colId === 'profitSpend') return roiToneClass(row.profitSpend);
  if (colId === 'profitExpense') return roiToneClass(row.profitExpense);
  if (colId === 'roiSpendPercent') return roiToneClass(row.roiSpendPercent);
  if (colId === 'roiExpensePercent') return roiToneClass(row.roiExpensePercent);
  return '';
}

/**
 * Nested country → ads account → package → date table.
 * Uses the same filter-card / report-table shell as DynamicReportTable.
 */
export default function RoiCountryTreeTable({
  title = 'Ads spend by country',
  tree = [],
  loading = false,
  search = '',
  onSearchChange,
  onPageReset,
  page = 1,
  pageSize = 25,
  onPageChange,
  density = 'comfortable',
  exportName = 'roi_countries',
  emptyMessage = 'No country spend for the selected filters',
  className = 'reporting-table',
  headerExtra = null,
  emptyActions = null,
  onReset = null,
  freezeFirst = true,
  canDownload = true,
  showPagination = true,
  showTotals = true,
  spendCurrency = 'USD',
  labelColumn = null,
  hideColumns = null,
  topIconKind = 'country',
  searchPlaceholder = 'Search country / account / package / site…',
}) {
  const COLS = useMemo(() => {
    const hidden = new Set(hideColumns || []);
    return BASE_COLS
      .filter((c) => !hidden.has(c.id))
      .map((c) => (c.id === 'label' && labelColumn ? { ...c, label: labelColumn } : c));
  }, [hideColumns, labelColumn]);
  const isMobile = useMedia('(max-width: 640px)');
  const [expandedCountries, setExpandedCountries] = useState(() => new Set());
  const [expandedAccounts, setExpandedAccounts] = useState(() => new Set());
  const [expandedPackages, setExpandedPackages] = useState(() => new Set());
  const [sortColumn, setSortColumn] = useState('adsSpend');
  const [sortDir, setSortDir] = useState('desc');

  const filteredTree = useMemo(
    () => filterCountryTree(tree, search),
    [tree, search]
  );

  const sortedTree = useMemo(
    () => sortCountryTree(filteredTree, sortColumn, sortDir),
    [filteredTree, sortColumn, sortDir]
  );

  const totals = useMemo(() => {
    const adsSpend = filteredTree.reduce((s, r) => s + (Number(r.adsSpend) || 0), 0);
    const impressions = filteredTree.reduce((s, r) => s + (Number(r.impressions) || 0), 0);
    const clicks = filteredTree.reduce((s, r) => s + (Number(r.clicks) || 0), 0);
    const earn = filteredTree.reduce((s, r) => s + (Number(r.earn) || 0), 0);
    const otherExpenses = filteredTree.reduce((s, r) => s + (Number(r.otherExpenses) || 0), 0);
    const profitSpend = earn - adsSpend;
    const profitExpense = earn - otherExpenses;
    const roiSpendPercent = adsSpend > 0 ? (profitSpend / adsSpend) * 100 : null;
    const roiExpensePercent = otherExpenses > 0 ? (profitExpense / otherExpenses) * 100 : null;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : null;
    const ecpm = impressions > 0 ? (adsSpend / impressions) * 1000 : null;
    return {
      adsSpend,
      impressions,
      clicks,
      ctr,
      ecpm,
      earn,
      otherExpenses,
      profitSpend,
      profitExpense,
      roiSpendPercent,
      roiExpensePercent,
    };
  }, [filteredTree]);

  const totalPages = Math.max(1, Math.ceil(sortedTree.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageCountries = sortedTree.slice((safePage - 1) * pageSize, safePage * pageSize);
  const colCount = COLS.length;
  const noData = !loading && filteredTree.length === 0;
  const showEmptyPanel = noData;

  const toggleCountry = (id) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAccount = (id) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePackage = (id) => {
    setExpandedPackages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(colId);
      setSortDir(colId === 'label' || colId === 'date' ? 'asc' : 'desc');
    }
    onPageChange?.(1);
  };

  const pageNumbers = () => {
    const arr = [];
    let start = Math.max(1, safePage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };

  const exportRows = () => {
    const flat = flattenCountryTreeForExport(filteredTree);
    const headers = [
      'Level', 'Name', 'Date', 'Ads spend', 'Earn', 'Expenses',
      'Profit (spend)', 'Profit (expenses)', 'ROI spend %', 'ROI expenses %',
    ];
    const body = flat.map((r) => [
      r.level,
      r.name,
      r.date || '',
      Number(r.adsSpend) || 0,
      Number(r.earn) || 0,
      Number(r.otherExpenses) || 0,
      Number(r.profitSpend) || 0,
      Number(r.profitExpense) || 0,
      r.roiSpendPercent == null ? '' : Number(r.roiSpendPercent),
      r.roiExpensePercent == null ? '' : Number(r.roiExpensePercent),
    ]);
    return { headers, body };
  };

  const copyTable = async () => {
    const { headers, body } = exportRows();
    const text = [
      headers.join('\t'),
      ...body.map((row) => row.map((c) => String(c).replace(/[\t\n]/g, ' ')).join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showToast({ message: `Copied ${body.length} row${body.length === 1 ? '' : 's'}` });
    } catch {
      showToast({ message: 'Could not copy table' });
    }
  };

  const downloadCsvFile = () => {
    const { headers, body } = exportRows();
    downloadCsv(exportName, headers, body);
    showToast({ message: `Downloaded ${body.length} row${body.length === 1 ? '' : 's'} (CSV)` });
  };

  const downloadExcelFile = () => {
    const { headers, body } = exportRows();
    downloadExcel(exportName, headers, body, title || 'Report');
    showToast({ message: `Downloaded ${body.length} row${body.length === 1 ? '' : 's'} (Excel)` });
  };

  const pagInfo = `Page ${safePage} of ${totalPages} · ${filteredTree.length.toLocaleString()} record${filteredTree.length === 1 ? '' : 's'}`;

  const tableClass = [
    'data-table',
    'responsive-table',
    'report-table',
    density === 'compact' ? 'report-table--compact' : 'report-table--comfortable',
    freezeFirst ? 'report-table--freeze-first' : '',
    'roi-country-tree-table',
    className,
  ].filter(Boolean).join(' ');

  const renderTreeRows = () => pageCountries.map((country) => {
    const countryOpen = expandedCountries.has(country.id);
    const flatMode = Boolean(country.flatMode);
    const childCount = flatMode
      ? (country.packages || []).length
      : (country.accountCount || (country.accounts || []).length);
    const hasChildren = childCount > 0;
    const childBadge = country.childBadge || (flatMode
      ? `${childCount} item${childCount === 1 ? '' : 's'}`
      : `${childCount} account${childCount === 1 ? '' : 's'}`);

    const renderPackageRows = (packages) => (packages || []).map((pkg) => {
      const pkgOpen = expandedPackages.has(pkg.id);
      const hasDays = (pkg.days || []).length > 1;
      return (
        <React.Fragment key={pkg.id}>
          <tr className={`roi-tree-row roi-tree-row--package${pkg.earnOnly || pkg.targetType === 'site' ? ' roi-tree-row--site' : ''}`}>
            <td className="roi-tree-label" data-label={COLS[0].label}>
              <button
                type="button"
                className={flatMode
                  ? 'roi-tree-toggle roi-tree-toggle--account'
                  : 'roi-tree-toggle roi-tree-toggle--package'}
                onClick={() => hasDays && togglePackage(pkg.id)}
                disabled={!hasDays}
                aria-expanded={hasDays ? pkgOpen : undefined}
              >
                <TreeChevron open={pkgOpen} hasChildren={hasDays} />
                <span className="roi-tree-label-main">
                  {pkg.hideIcon ? null : (
                    <RoiTreeEntityIcon
                      kind={pkg.targetType === 'site' || pkg.earnOnly ? 'site' : 'app'}
                      label={pkg.label}
                    />
                  )}
                  <span className="roi-tree-label-text">{pkg.label}</span>
                </span>
                <span className="roi-tree-kind">
                  {pkg.kindLabel || (pkg.targetType === 'site' || pkg.earnOnly ? 'Site' : 'Package')}
                </span>
                {hasDays ? (
                  <span className="roi-tree-badge">{pkg.days.length} days</span>
                ) : null}
              </button>
            </td>
            <td data-label={COLS[1].label}>{pkg.dateLabel || '—'}</td>
            {COLS.slice(2).map((col) => (
              <td key={col.id} data-label={col.label} className={metricCellClass(pkg, col.id) || undefined}>
                {renderMetric(pkg, col.id, spendCurrency)}
              </td>
            ))}
          </tr>

          {pkgOpen && (pkg.days || []).map((day) => (
            <tr key={day.id} className="roi-tree-row roi-tree-row--date">
              <td className="roi-tree-label" data-label={COLS[0].label}>
                <div className={flatMode
                  ? 'roi-tree-toggle roi-tree-toggle--package'
                  : 'roi-tree-toggle roi-tree-toggle--date'}
                >
                  <TreeChevron open={false} hasChildren={false} />
                  <span className="roi-tree-label-text">{day.date}</span>
                </div>
              </td>
              <td data-label={COLS[1].label}>{day.date || '—'}</td>
              {COLS.slice(2).map((col) => (
                <td key={col.id} data-label={col.label} className={metricCellClass(day, col.id) || undefined}>
                  {renderMetric(day, col.id, spendCurrency)}
                </td>
              ))}
            </tr>
          ))}
        </React.Fragment>
      );
    });

    return (
      <React.Fragment key={country.id}>
        <tr className="roi-tree-row roi-tree-row--country">
          <td className="roi-tree-label" data-label={COLS[0].label}>
            <button
              type="button"
              className="roi-tree-toggle"
              onClick={() => hasChildren && toggleCountry(country.id)}
              disabled={!hasChildren}
              aria-expanded={hasChildren ? countryOpen : undefined}
            >
              <TreeChevron open={countryOpen} hasChildren={hasChildren} />
              <span className="roi-tree-label-main">
                <RoiTreeEntityIcon
                  kind={country.iconKind || topIconKind}
                  code={country.countryCode}
                  label={country.label}
                />
                <span className="roi-tree-label-text">{country.label}</span>
                {country.subLabel ? (
                  <span className="roi-tree-kind">{country.subLabel}</span>
                ) : null}
              </span>
              {hasChildren ? (
                <span className="roi-tree-badge">{childBadge}</span>
              ) : null}
              {(Number(country.appEarn) > 0 || Number(country.siteEarn) > 0) ? (
                <span className="roi-tree-earn-split" title="App earn + Site earn = total Earn">
                  App {money(country.appEarn, spendCurrency)}
                  {' · '}
                  Site {money(country.siteEarn, spendCurrency)}
                </span>
              ) : null}
            </button>
          </td>
          <td data-label={COLS[1].label}>{country.dateLabel || '—'}</td>
          {COLS.slice(2).map((col) => (
            <td key={col.id} data-label={col.label} className={metricCellClass(country, col.id) || undefined}>
              {renderMetric(country, col.id, spendCurrency)}
            </td>
          ))}
        </tr>

        {countryOpen && flatMode && renderPackageRows(country.packages)}

        {countryOpen && !flatMode && (country.accounts || []).map((account) => {
          const accountOpen = expandedAccounts.has(account.id);
          const hasPackages = (account.packages || []).length > 0;
          return (
            <React.Fragment key={account.id}>
              <tr className="roi-tree-row roi-tree-row--account">
                <td className="roi-tree-label" data-label={COLS[0].label}>
                  <button
                    type="button"
                    className="roi-tree-toggle roi-tree-toggle--account"
                    onClick={() => hasPackages && toggleAccount(account.id)}
                    disabled={!hasPackages}
                    aria-expanded={hasPackages ? accountOpen : undefined}
                  >
                    <TreeChevron open={accountOpen} hasChildren={hasPackages} />
                    <span className="roi-tree-label-text">
                      {account.customerId && account.customerId !== account.label
                        ? `${account.label} (${account.customerId})`
                        : account.label}
                    </span>
                    <span className="roi-tree-kind">Ads account</span>
                  </button>
                </td>
                <td data-label={COLS[1].label}>{account.dateLabel || '—'}</td>
                {COLS.slice(2).map((col) => (
                  <td key={col.id} data-label={col.label} className={metricCellClass(account, col.id) || undefined}>
                    {renderMetric(account, col.id, spendCurrency)}
                  </td>
                ))}
              </tr>

              {accountOpen && renderPackageRows(account.packages)}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  return (
    <div className="filter-card">
      <div className="filter-card-head">
        <span className="filter-card-title">{title}</span>
        <div className="filter-actions">
          {onSearchChange && (
            <TableSearchBar
              value={search}
              onChange={onSearchChange}
              onPageReset={onPageReset}
              placeholder={searchPlaceholder}
            />
          )}
          {headerExtra}
          {filteredTree.length > 0 && (
            <button type="button" className="btn-reset table-tool-btn" onClick={copyTable}>
              Copy
            </button>
          )}
          {canDownload && filteredTree.length > 0 && (
            <>
              <button type="button" className="btn-reset table-tool-btn" onClick={downloadCsvFile}>
                CSV
              </button>
              <button type="button" className="btn-reset table-tool-btn" onClick={downloadExcelFile}>
                Excel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="table-wrap">
        {showEmptyPanel && !loading ? (
          <div className="gam-report-empty">
            <div className="gam-report-empty-icon" aria-hidden><EmptyIcon size={40} /></div>
            <p className="gam-report-empty-title">{emptyMessage}</p>
            <p className="gam-report-empty-hint">
              Try a different date range, or clear a filter and click Apply Filter.
            </p>
            <div className="dash-empty-actions" style={{ marginTop: 14 }}>
              {emptyActions}
              {onReset && (
                <button type="button" className="btn-reset" onClick={onReset}>
                  Reset filters
                </button>
              )}
            </div>
          </div>
        ) : isMobile && !loading && filteredTree.length > 0 ? (
          <div className="report-mobile-list">
            {pageCountries.map((country) => (
              <article key={country.id} className="report-mobile-item">
                <div className="report-mobile-item-head">
                  <p className="report-mobile-title report-mobile-title--with-icon">
                    <RoiTreeEntityIcon
                      kind={country.iconKind || topIconKind}
                      code={country.countryCode}
                      label={country.label}
                    />
                    <span>{country.label}</span>
                  </p>
                  <p className="report-mobile-sub">{country.dateLabel}</p>
                </div>
                <dl className="report-mobile-metrics">
                  {COLS.slice(2).map((col) => (
                    <div key={col.id} className="report-mobile-metric">
                      <dt>{col.label}</dt>
                      <dd className={metricCellClass(country, col.id) || undefined}>
                        {renderMetric(country, col.id, spendCurrency)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <table className={tableClass}>
            <thead>
              <tr>
                {COLS.map((col) => (
                  <th key={col.id}>
                    <SortHeader
                      label={col.label}
                      active={sortColumn === col.id}
                      dir={sortDir}
                      onClick={() => handleSort(col.id)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: colCount }).map((_, j) => (
                      <td key={j} data-label="">
                        <div className="skeleton" style={{ height: 16 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                renderTreeRows()
              )}
            </tbody>
            {showTotals && !loading && !showEmptyPanel && filteredTree.length > 0 && (
              <tfoot>
                <tr className="report-total-row">
                  <td>Total</td>
                  <td>—</td>
                  {COLS.slice(2).map((col) => (
                    <td key={col.id} className={metricCellClass(totals, col.id) || undefined}>
                      {renderMetric(totals, col.id, spendCurrency)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>

      {showPagination && !loading && filteredTree.length > 0 && onPageChange && (
        <div className="pagination">
          <span className="pag-info">{pagInfo}</span>
          <div className="pag-btns">
            <button
              type="button"
              className="pag-btn"
              disabled={safePage === 1}
              onClick={() => onPageChange(1)}
              title="First page"
            >
              «
            </button>
            <button
              type="button"
              className="pag-btn"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
              title="Previous page"
            >
              ‹
            </button>
            {pageNumbers().map((p) => (
              <button
                key={p}
                type="button"
                className={`pag-btn ${safePage === p ? 'active' : ''}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className="pag-btn"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
              title="Next page"
            >
              ›
            </button>
            <button
              type="button"
              className="pag-btn"
              disabled={safePage === totalPages}
              onClick={() => onPageChange(totalPages)}
              title="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
